var _a, _b;
import { useMemo } from 'react';
import { useGetFamilyImpactApi } from './api/family/useGetFamilyImpact.api';
import { KpiType } from '../models';
/**
 * Mapping KpiType to localization label keys
 */
var KPI_TYPE_TO_LABEL_KEY = (_a = {},
    _a[KpiType.WATER] = 'environmentalImpact.kpi_water_saved',
    _a[KpiType.CO2] = 'environmentalImpact.kpi_co2_not_emitted',
    _a[KpiType.ANIMALS_SAVED] = 'environmentalImpact.kpi_animals_saved',
    _a[KpiType.FOREST_PROTECTED] = 'environmentalImpact.kpi_forest_protected',
    _a[KpiType.GRAIN] = 'environmentalImpact.kpi_grain_not_consumed',
    _a);
/**
 * Mapping KpiType to default display units
 */
var KPI_TYPE_TO_UNIT = (_b = {},
    _b[KpiType.WATER] = 'L',
    _b[KpiType.CO2] = 'kg',
    _b[KpiType.ANIMALS_SAVED] = '',
    _b[KpiType.FOREST_PROTECTED] = 'm²',
    _b[KpiType.GRAIN] = 'kg',
    _b);
/**
 * Helper function to aggregate KPIs by type
 */
var aggregateKpis = function (kpis) {
    if (!Array.isArray(kpis) || kpis.length === 0) {
        return [];
    }
    var aggregated = new Map();
    // Sum values by type
    for (var _i = 0, kpis_1 = kpis; _i < kpis_1.length; _i++) {
        var kpi = kpis_1[_i];
        if (!kpi ||
            typeof kpi.type === 'undefined' ||
            typeof kpi.value !== 'number' ||
            isNaN(kpi.value)) {
            continue;
        }
        // Skip if type is not a valid KpiType
        if (!Object.values(KpiType).includes(kpi.type)) {
            continue;
        }
        var existing = aggregated.get(kpi.type);
        if (existing) {
            aggregated.set(kpi.type, {
                totalValue: existing.totalValue + kpi.value,
                unit: kpi.unit || existing.unit,
            });
        }
        else {
            aggregated.set(kpi.type, {
                totalValue: kpi.value,
                unit: kpi.unit || KPI_TYPE_TO_UNIT[kpi.type],
            });
        }
    }
    // Map to AggregatedImpact and sort
    var order = [
        KpiType.WATER,
        KpiType.CO2,
        KpiType.ANIMALS_SAVED,
        KpiType.FOREST_PROTECTED,
        KpiType.GRAIN,
    ];
    return Array.from(aggregated.entries())
        .map(function (_a) {
        var type = _a[0], _b = _a[1], totalValue = _b.totalValue, unit = _b.unit;
        return ({
            type: type,
            totalValue: totalValue,
            labelKey: KPI_TYPE_TO_LABEL_KEY[type],
            unit: unit,
        });
    })
        .sort(function (a, b) { return order.indexOf(a.type) - order.indexOf(b.type); });
};
/**
 * Custom hook for Family Environmental Impact
 * Fetches family impact data and provides stats for logged member and family total
 *
 * @param familyId - Family UID identifier
 * @param loggedPersonId - Optional person ID of the logged-in user
 * @returns Object containing raw data, loading state, error, member stats, and family stats
 */
export var useFamilyImpact = function (familyId, loggedPersonId) {
    var _a = useGetFamilyImpactApi(familyId), data = _a.data, isLoading = _a.isLoading, error = _a.error;
    /**
     * KPIs for the logged-in member only
     */
    var memberStats = useMemo(function () {
        if (!(data === null || data === void 0 ? void 0 : data.members) || !Array.isArray(data.members) || !loggedPersonId) {
            return [];
        }
        var loggedMember = data.members.find(function (member) { return member && member.id === loggedPersonId; });
        if (!(loggedMember === null || loggedMember === void 0 ? void 0 : loggedMember.kpis) || !Array.isArray(loggedMember.kpis)) {
            return [];
        }
        return aggregateKpis(loggedMember.kpis);
    }, [data, loggedPersonId]);
    /**
     * Aggregated KPIs across all family members
     */
    var familyStats = useMemo(function () {
        if (!(data === null || data === void 0 ? void 0 : data.members) || !Array.isArray(data.members)) {
            return [];
        }
        // Collect all KPIs from all members
        var allKpis = [];
        for (var _i = 0, _a = data.members; _i < _a.length; _i++) {
            var member = _a[_i];
            if (!member || !member.kpis || !Array.isArray(member.kpis)) {
                continue;
            }
            for (var _b = 0, _c = member.kpis; _b < _c.length; _b++) {
                var kpi = _c[_b];
                if (kpi &&
                    typeof kpi.type !== 'undefined' &&
                    typeof kpi.value === 'number') {
                    allKpis.push(kpi);
                }
            }
        }
        return aggregateKpis(allKpis);
    }, [data]);
    return {
        data: data,
        isLoading: isLoading,
        error: error,
        memberStats: memberStats,
        familyStats: familyStats,
    };
};
